/**
 * Comparison Storage Utilities for localStorage persistence
 */

import { ComparisonItem, ComparisonState, ComparisonCustomMetrics } from '@/types/comparison';
import { Vendor } from '@/types/vendor';

const STORAGE_KEY = 'v2v2-comparison';
const CURRENT_VERSION = 1;

/**
 * Default comparison state
 */
export function getDefaultComparisonState(): ComparisonState {
  return {
    items: [],
    isModalOpen: false,
    isLoading: false,
    error: null,
  };
}

/**
 * Save comparison state to localStorage
 */
export function saveComparisonState(state: ComparisonState): void {
  try {
    const data = JSON.stringify({
      ...state,
      lastUpdated: new Date().toISOString(),
      version: CURRENT_VERSION,
    });
    localStorage.setItem(STORAGE_KEY, data);
  } catch (error) {
    console.error('Failed to save comparison state to localStorage:', error);
    throw new Error('Unable to save comparison. Please check your storage permissions.');
  }
}

/**
 * Load comparison state from localStorage
 */
export function loadComparisonState(): ComparisonState {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return getDefaultComparisonState();
    }

    const parsed = JSON.parse(data);
    
    // Validate structure
    if (!isValidComparisonState(parsed)) {
      console.warn('Invalid comparison state found, resetting...');
      clearComparisonState();
      return getDefaultComparisonState();
    }

    return {
      items: parsed.items || [],
      isModalOpen: false, // Always reset modal state on load
      isLoading: false,
      error: null,
    };
  } catch (error) {
    console.error('Failed to load comparison state from localStorage:', error);
    return getDefaultComparisonState();
  }
}

/**
 * Check if a parsed object is a valid comparison state
 */
function isValidComparisonState(data: unknown): data is { items: ComparisonItem[] } {
  if (!data || typeof data !== 'object') return false;
  
  const state = data as Record<string, unknown>;
  
  if (!Array.isArray(state.items)) return false;
  
  // Validate each item
  return state.items.every((item) => 
    typeof item === 'object' &&
    typeof item.id === 'string' &&
    typeof item.type === 'string' &&
    item.type === 'vendor' &&
    typeof item.data === 'object'
  );
}

/**
 * Clear comparison state from localStorage
 */
export function clearComparisonState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear comparison state from localStorage:', error);
    throw new Error('Unable to clear comparison.');
  }
}

/**
 * Add an item to comparison
 * Returns true if added, false if already exists or at max capacity
 */
export function addComparisonItem(
  item: ComparisonItem,
  currentItems: ComparisonItem[],
  maxItems: number = 4
): { success: boolean; items: ComparisonItem[]; error?: string } {
  // Check if already exists
  if (currentItems.some((i) => i.id === item.id)) {
    return { success: false, items: currentItems, error: 'Item already in comparison' };
  }
  
  // Check max capacity
  if (currentItems.length >= maxItems) {
    return { success: false, items: currentItems, error: 'Maximum items reached' };
  }
  
  const updatedItems = [...currentItems, item];
  return { success: true, items: updatedItems };
}

/**
 * Remove an item from comparison
 * Returns true if removed, false if not found
 */
export function removeComparisonItem(
  id: string,
  currentItems: ComparisonItem[]
): { success: boolean; items: ComparisonItem[] } {
  const initialLength = currentItems.length;
  const updatedItems = currentItems.filter((item) => item.id !== id);
  
  if (updatedItems.length === initialLength) {
    return { success: false, items: currentItems };
  }
  
  return { success: true, items: updatedItems };
}

/**
 * Check if an item is in comparison
 */
export function isInComparison(id: string, items: ComparisonItem[]): boolean {
  return items.some((item) => item.id === id);
}

/**
 * Toggle an item in comparison
 */
export function toggleComparisonItem(
  item: ComparisonItem,
  currentItems: ComparisonItem[],
  maxItems: number = 4
): { success: boolean; items: ComparisonItem[]; added: boolean; error?: string } {
  const existingIndex = currentItems.findIndex((i) => i.id === item.id);
  
  if (existingIndex >= 0) {
    // Remove if exists
    const updatedItems = [
      ...currentItems.slice(0, existingIndex),
      ...currentItems.slice(existingIndex + 1),
    ];
    return { success: true, items: updatedItems, added: false };
  }
  
  // Add if doesn't exist
  const result = addComparisonItem(item, currentItems, maxItems);
  return {
    success: result.success,
    items: result.items,
    added: result.success,
    error: result.error,
  };
}

/**
 * Clear all items from comparison
 */
export function clearComparisonItems(): ComparisonItem[] {
  return [];
}

/**
 * Get count of items in comparison
 */
export function getComparisonCount(items: ComparisonItem[]): number {
  return items.length;
}

/**
 * Export comparison as JSON string
 */
export function exportComparisonState(items: ComparisonItem[]): string {
  return JSON.stringify({
    items,
    lastUpdated: new Date().toISOString(),
    version: CURRENT_VERSION,
  }, null, 2);
}

/**
 * Import comparison from JSON string
 */
export function importComparisonState(
  json: string
): { success: boolean; items: ComparisonItem[]; errors: string[] } {
  const errors: string[] = [];
  let items: ComparisonItem[] = [];

  try {
    const data = JSON.parse(json);
    
    if (!isValidComparisonState(data)) {
      return { success: false, items: [], errors: ['Invalid comparison format'] };
    }

    items = data.items;
    return { success: true, items, errors };
  } catch (error) {
    return {
      success: false,
      items: [],
      errors: errors.length > 0 ? errors : ['Failed to parse comparison JSON'],
    };
  }
}

/**
 * Create a ComparisonItem from a Vendor
 */
export function createComparisonItemFromVendor(
  vendor: Vendor,
  customMetrics?: ComparisonCustomMetrics
): ComparisonItem {
  return {
    id: vendor.id,
    type: 'vendor',
    data: vendor,
    customMetrics,
  };
}
